import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/authStore';
import { MicroLabel } from '@/components/brand';
import { PageSEO } from '@/components/SEO';
import { Logo } from '@/components/ui/Logo';
import api from '@/services/api';
import { Eye, EyeOff, Loader2, Mail } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setResendSuccess(false);
    setIsLoading(true);

    try {
      const status = await login(email, password, rememberMe);
      
      // Get fresh user data from store
      const user = useAuthStore.getState().user;
      
      // Where a logged-in user lands.
      //
      // This used to send EVERYONE to /tool, which contradicted both the product
      // (CLAUDE.md: the Feed is the logged-in front door) and the app's own
      // behaviour — PublicRoute already bounces an authenticated user hitting a
      // public route to /feed. So where you ended up depended on how you got in.
      //
      // Now: not onboarded -> finish setting up. Onboarded -> the Feed, where
      // Zupa has drafts waiting. A workspace that isn't set up yet would only
      // find an empty feed, so those users still go to the tool.
      if (user?.is_pro && status && !status.is_onboarded) {
        navigate('/onboarding');
      } else if (status?.is_onboarded) {
        navigate('/feed');
      } else {
        navigate('/tool');
      }
    } catch (err: unknown) {
      console.error('Login error:', err);
      
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string }, status?: number } };
        const errorMessage = axiosError.response?.data?.detail || 'Login failed. Please try again.';
        setError(errorMessage);
        
        if (errorMessage.toLowerCase().includes('verify your email')) {
          setNeedsVerification(true);
        }
      } else if (err instanceof Error) {
        setError(err.message || 'Login failed. Please try again.');
      } else {
        setError('Login failed. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    
    setIsResendingVerification(true);
    setError(null);
    
    try {
      await api.resendVerification(email);
      setResendSuccess(true);
      setNeedsVerification(false);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setError(axiosError.response?.data?.detail || 'Failed to resend verification email.');
      } else {
        setError('Failed to resend verification email.');
      }
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { authorization_url } = await api.googleAuthUrl();
      window.location.href = authorization_url;
    } catch {
      setError('Failed to initialize Google login.');
    }
  };

  return (
    <div className="min-h-screen bg-paper dark:bg-dark-bg flex flex-col items-center justify-center px-6 relative overflow-hidden transition-colors">
      <PageSEO.Login />

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md mx-auto animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Link to="/">
            <Logo variant="icon-wordmark" className="h-12" />
          </Link>
          <p className="font-body font-light text-ink dark:text-dark-text text-xl sm:text-2xl">
            Turbocharge Your Communication to Your Target Audience.
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white dark:bg-dark-surface rounded-lg border border-hairline dark:border-dark-border p-8 shadow-subtle transition-colors">
          <h2 className="font-display text-ink dark:text-dark-text text-xl sm:text-2xl mb-6 text-center leading-snug">
            Welcome back
          </h2>

          {/* Success message for resent verification */}
          {resendSuccess && (
            <div className="mb-4 p-3 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>Verification email sent! Please check your inbox.</span>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 rounded-2xl bg-error-bg dark:bg-red-900/20 border border-error-border dark:border-red-800 text-error-text dark:text-red-400 text-sm">
              <p>{error}</p>
              {needsVerification && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={isResendingVerification}
                  className="mt-2 flex items-center gap-2 text-ink dark:text-dark-text font-medium hover:underline disabled:opacity-50"
                >
                  {isResendingVerification ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Resend verification email
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <MicroLabel as="label" htmlFor="email">Email</MicroLabel>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="dark:bg-dark-bg dark:border-dark-border dark:text-dark-text dark:placeholder:text-dark-muted"
              />
            </div>

            <div className="space-y-2">
              <MicroLabel as="label" htmlFor="password">Password</MicroLabel>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="pr-10 dark:bg-dark-bg dark:border-dark-border dark:text-dark-text dark:placeholder:text-dark-muted"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-dark dark:text-dark-muted hover:text-ink dark:hover:text-dark-text transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-hairline dark:border-dark-border text-ink focus:ring-ink dark:bg-dark-bg"
                />
                <span className="text-sm text-ink dark:text-dark-text">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-ink dark:text-dark-text hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full bg-cargo hover:bg-cargo/90 text-cargo-ink rounded-md focus-visible:ring-ink"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Log In'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-hairline dark:border-dark-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-dark-surface text-steel-dark dark:text-dark-muted">or</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full dark:border-dark-border dark:text-dark-text dark:hover:bg-dark-bg"
            size="lg"
            onClick={handleGoogleLogin}
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-sm text-steel-dark dark:text-dark-muted">
            Don't have an account?{' '}
            <Link to="/register" className="text-ink dark:text-dark-text font-medium hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
